import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import Layout from "../../components/Layout";
import {
  ListEmptyState,
  ListTableLoading,
} from "../../components/ListSearchFilters";
import { ListPagination } from "../../components/ListPagination";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import {
  LIST_SEARCH_DEBOUNCE_MS,
  shouldSyncSearchToFirstPage,
} from "../../utils/listFilterQuery";
import { PERM } from "../../utils/permissions";
import { NotificationsAutomationsSection } from "./NotificationsAutomationsSection";
import { NotificationsListFilters } from "./NotificationsListFilters";
import { NotificationsListTable } from "./NotificationsListTable";
import { NotificationsTabs } from "./NotificationsTabs";
import {
  AutomationCategory,
  NotificationAutomation,
  NotificationLogItem,
  NotificationTab,
  buildAutomationOptions,
  buildNotificationsPath,
  parseDateFilter,
  parseNotificationChannelFilter,
  parseNotificationStatusFilter,
  resolveDefaultNotificationTab,
} from "./notificationsListQuery";

interface IParams {
  page: string;
}

export default function Notifications() {
  const history = useHistory();
  const location = useLocation();
  const { params } = useRouteMatch<IParams>();
  const { dateToExpires, access_token, user, hasPermission } = useAuth();
  const { addToast } = useToast();

  const canViewAutomations = hasPermission(PERM.notificationsAutomations);
  const canViewLogs = hasPermission(PERM.notificationsLogs);

  const [activeTab, setActiveTab] = useState<NotificationTab>(() =>
    resolveDefaultNotificationTab(canViewLogs, canViewAutomations)
  );

  const currentPage = Number(params.page) || 1;
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const channelFilter = parseNotificationChannelFilter(query.get("channel"));
  const statusFilter = parseNotificationStatusFilter(query.get("status"));
  const automationFilter = (query.get("automation") ?? "").trim();
  const fromDate = parseDateFilter(query.get("from_date"));
  const toDate = parseDateFilter(query.get("to_date"));
  const urlRecipient = (query.get("recipient") ?? "").trim();

  const [recipientTerm, setRecipientTerm] = useState(urlRecipient);
  const [debouncedRecipient, setDebouncedRecipient] = useState(urlRecipient);
  const [automationCategory, setAutomationCategory] = useState<AutomationCategory>("business");

  const [automations, setAutomations] = useState<NotificationAutomation[]>([]);
  const [automationsLoading, setAutomationsLoading] = useState(canViewAutomations);

  const [logs, setLogs] = useState<NotificationLogItem[]>([]);
  const [lastPage, setLastPage] = useState(0);
  const [logsLoading, setLogsLoading] = useState(canViewLogs);

  const filterSnapshot = useMemo(
    () => ({
      recipient: debouncedRecipient,
      channel: channelFilter,
      status: statusFilter,
      automation: automationFilter,
      fromDate,
      toDate,
    }),
    [
      debouncedRecipient,
      channelFilter,
      statusFilter,
      automationFilter,
      fromDate,
      toDate,
    ]
  );

  const automationsByKey = useMemo(
    () => new Map(automations.map((automation) => [automation.key, automation])),
    [automations]
  );

  const automationOptions = useMemo(
    () => buildAutomationOptions(automations),
    [automations]
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedRecipient(recipientTerm.trim());
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [recipientTerm]);

  useEffect(() => {
    if (!shouldSyncSearchToFirstPage(debouncedRecipient, urlRecipient)) return;

    const nextUrl = buildNotificationsPath(1, filterSnapshot);
    const currentUrl = `${history.location.pathname}${history.location.search}`;
    if (currentUrl !== nextUrl) history.push(nextUrl);
  }, [debouncedRecipient, filterSnapshot, urlRecipient, history]);

  useEffect(() => {
    if (!user) {
      history.push("/");
      return;
    }

    if (!canViewAutomations) {
      setAutomations([]);
      setAutomationsLoading(false);
      return;
    }

    async function loadAutomations() {
      setAutomationsLoading(true);
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

        const response = await api.get("/api/adm/notifications/automations", {
          params: {
            ...(automationCategory ? { category: automationCategory } : {}),
          },
        });

        setAutomations(response.data.data.automations ?? []);
      } catch (err: any) {
        if (err.response?.data?.message) {
          addToast({
            title: err.response.data.message,
            type: "error",
          });
        }
      } finally {
        setAutomationsLoading(false);
      }
    }

    loadAutomations();
  }, [
    user,
    access_token,
    dateToExpires,
    history,
    automationCategory,
    canViewAutomations,
    addToast,
  ]);

  useEffect(() => {
    if (!user) return;
    if (!canViewLogs) {
      setLogs([]);
      setLogsLoading(false);
      return;
    }

    if (shouldSyncSearchToFirstPage(debouncedRecipient, urlRecipient)) return;

    async function loadLogs() {
      setLogsLoading(true);
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

        const response = await api.get("/api/adm/notifications/logs", {
          params: {
            page: currentPage,
            ...(debouncedRecipient ? { recipient: debouncedRecipient } : {}),
            ...(channelFilter ? { channel: channelFilter } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(automationFilter ? { automation: automationFilter } : {}),
            ...(fromDate ? { from_date: fromDate } : {}),
            ...(toDate ? { to_date: toDate } : {}),
          },
        });

        setLogs(response.data.data.logs.data ?? []);
        setLastPage(Number(response.data.data.logs.last_page) || 0);
      } catch (err: any) {
        if (err.response?.data?.message) {
          addToast({
            title: err.response.data.message,
            type: "error",
          });
        }
      } finally {
        setLogsLoading(false);
      }
    }

    loadLogs();
  }, [
    user,
    access_token,
    dateToExpires,
    currentPage,
    debouncedRecipient,
    urlRecipient,
    channelFilter,
    statusFilter,
    automationFilter,
    fromDate,
    toDate,
    canViewLogs,
    addToast,
  ]);

  function pushFilters(
    page: number,
    overrides: Partial<typeof filterSnapshot> = {}
  ) {
    history.push(
      buildNotificationsPath(page, {
        ...filterSnapshot,
        ...overrides,
      })
    );
  }

  const historyPanel = canViewLogs ? (
    <>
      <p className="mb-3 text-sm leading-snug text-slate-500">
        Registros persistidos no momento do envio (Enviado / Falhou / Ignorado).
      </p>

      <NotificationsListFilters
        recipientTerm={recipientTerm}
        channelFilter={channelFilter}
        statusFilter={statusFilter}
        automationFilter={automationFilter}
        fromDate={fromDate}
        toDate={toDate}
        automationOptions={automationOptions}
        onRecipientChange={setRecipientTerm}
        onChannelChange={(value) => pushFilters(1, { channel: value })}
        onStatusChange={(value) => pushFilters(1, { status: value })}
        onAutomationChange={(value) => pushFilters(1, { automation: value })}
        onFromDateChange={(value) => pushFilters(1, { fromDate: value })}
        onToDateChange={(value) => pushFilters(1, { toDate: value })}
      />

      {logsLoading ? (
        <ListTableLoading />
      ) : logs.length === 0 ? (
        <ListEmptyState message="Nenhum envio registrado ainda. Os registros aparecem aqui após a primeira automação disparar." />
      ) : (
        <NotificationsListTable logs={logs} automationsByKey={automationsByKey} />
      )}

      <ListPagination
        currentPage={currentPage}
        totalPages={lastPage}
        onPageChange={(page) => pushFilters(page)}
      />
    </>
  ) : null;

  const automationsPanel = canViewAutomations ? (
    <NotificationsAutomationsSection
      automations={automations}
      categoryFilter={automationCategory}
      loading={automationsLoading}
      onCategoryChange={setAutomationCategory}
    />
  ) : null;

  return (
    <Layout>
      <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col px-2 py-4 font-[inherit] md:px-3 md:py-5">
        <div className="mb-4">
          <h3 className="text-2xl font-bold text-slate-800">Notificações</h3>
          <p className="mt-1 text-sm text-slate-500">
            Acompanhe automações de e-mail e WhatsApp e consulte o histórico de envios com sucessos e falhas.
          </p>
        </div>

        {canViewLogs && canViewAutomations ? (
          <NotificationsTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            showAutomationsTab
            historyPanel={historyPanel}
            automationsPanel={automationsPanel}
          />
        ) : canViewLogs ? (
          historyPanel
        ) : (
          automationsPanel
        )}
      </div>
    </Layout>
  );
}
